import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardImage,
  Icon,
} from '@idexx-laboratories/spot-react';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import ContentHeader from '../../components/ContentHeader/ContentHeader';
import Loader from '../../components/Loader/Loader';
import { LoadingSpinnerWithContent } from '../../components/LoadingSpinnerWithContent/LoadingSpinnerWithContent';
import { useStore } from '../../hooks/use-store';
import { useCheckSingleSapPermission } from '../../security/security-hook';
import {
  PERMISSION_ALLOW,
  PERMISSION_VIEW,
  RESOURCE_DASHBOARD_BUTTON_SCHEDULE_NOW,
  RESOURCE_DASHBOARD_BYPASS_ACKNOWLEDGEMENTS,
  RESOURCE_LEFT_NAV_PRACTICE_USERS_MNGMNT,
} from '../../types/users-types';
import { openInANewWindow } from '../../utils/url-utils';
import './Dashboard.scss';

const Dashboard = observer(() => {
  const { dashboardStore: store, apiStore, globalStore } = useStore();
  const { sapIds, loaders, buttonsDisabled, velloAuthLinks } = store;
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const allowUserManagement = useCheckSingleSapPermission(
    sapIds,
    RESOURCE_LEFT_NAV_PRACTICE_USERS_MNGMNT,
    PERMISSION_VIEW,
  );
  const allowScheduleNow = useCheckSingleSapPermission(
    sapIds,
    RESOURCE_DASHBOARD_BUTTON_SCHEDULE_NOW,
    PERMISSION_ALLOW,
  );
  const bypassAcknowledgements = useCheckSingleSapPermission(
    sapIds,
    RESOURCE_DASHBOARD_BYPASS_ACKNOWLEDGEMENTS,
    PERMISSION_ALLOW,
  );

  useEffect(() => {
    if (apiStore.isAuthenticated) {
      void store.init();
    }
    return () => store.close();
  }, [apiStore.isAuthenticated, store]);

  useEffect(() => {
    setSelectedIndex(globalStore.userSaps.findIndex(us => us.id === globalStore.selectedSap.id));
  }, [globalStore.selectedSap.id, globalStore.userSaps]);

  const focusRef = useCallback(
    (el: HTMLDivElement | null, index) => {
      if (index === selectedIndex) el?.scrollIntoView({ block: 'center' });
    },
    [selectedIndex],
  );

  if (loaders.page)
    return (
      <LoadingSpinnerWithContent size='large'>
        One moment while we retrieve your affiliated practices
      </LoadingSpinnerWithContent>
    );
  return (
    <div className='dashboard-wrapper'>
      {globalStore.userSaps.length > 0 && (
        <>
          <div className='spot-typography__heading--level-1'>
            Welcome to MyCornerstone Self Service Portal
          </div>
          <div className='spot-typography__text--body'>
            Here you can: <br />
            <ul>
              <li>Set up and manage approved integrations with your Cornerstone software</li>
              <li>Visit the Cornerstone Online Knowledgebase</li>
              <li>
                View practices and manage practice users and their permissions with your MyIDEXX
                account
              </li>
              <li>Check your system settings and schedule your automatic Cornerstone upgrade</li>
            </ul>
          </div>
        </>
      )}
      {globalStore.userSaps?.map((sap, index) => (
        <div className='business' key={sap.id} ref={el => focusRef(el, index)}>
          <ContentHeader
            className='spot-typography__heading--level-5 expand-toggle'
            onClick={() => globalStore.changeSelectedSap(sap.id)}
            data-testid={`expandable-${sap.id}`}
          >
            {globalStore.selectedSap.id === sap.id ? (
              <Icon className='caret-icon' iconName='caret-up' title='collapse' />
            ) : (
              <Icon className='caret-icon' iconName='caret-down' title='expand' />
            )}
            &nbsp;{sap.name} ({sap.id})
          </ContentHeader>
          <div
            className={`business-cards ${globalStore.selectedSap.id !== sap.id ? 'collapsed' : ''}`}
          >
            {sap.integrations?.vello && (
              <Card className='business-card' variant='primary'>
                <CardImage src='/images/vello_icon.svg' alt='Vello' alignment='top-center' />
                <CardBody alignment='top-center'>
                  <div className='card-body-title'>Client Engagement Platform</div>
                </CardBody>
                <CardFooter alignment='top-center'>
                  <Button
                    variant='primary'
                    size='small'
                    aria-label='Vello Launch'
                    onClick={() => store.launchVelloAuthPage(sap.id)}
                    data-testid={`vello-${sap.id}-btn`}
                    disabled={velloAuthLinks[sap.id]?.loading}
                  >
                    <Loader loading={velloAuthLinks[sap.id]?.loading}>Open</Loader>
                  </Button>
                </CardFooter>
              </Card>
            )}
            <Card className='business-card' variant='primary'>
              <CardImage src='/images/self_help_icon.svg' alt='Self Help' alignment='top-center' />
              <CardBody alignment='top-center'>
                <div className='card-body-title'>Cornerstone Help Hub</div>
              </CardBody>
              <CardFooter alignment='top-center'>
                <Button
                  variant='primary'
                  size='small'
                  aria-label='Helphub Explore'
                  onClick={() => openInANewWindow('https://cornerstonehelphub.com/')}
                  data-testid={`explore-${sap.id}-btn`}
                >
                  Explore
                </Button>
              </CardFooter>
            </Card>
            <Card className='business-card' variant='primary'>
              <CardImage
                src='/images/user_management_icon.svg'
                alt='User Management'
                alignment='top-center'
              />
              <CardBody alignment='top-center'>
                <div className='card-body-title'>User Management</div>
              </CardBody>
              <CardFooter alignment='top-center'>
                <Button
                  variant='primary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() =>
                    void store.checkAllowUserManagement(index, allowUserManagement[sap.id])
                  }
                  data-testid={`user-mgmt-${sap.id}-btn`}
                >
                  Manage Users
                </Button>
              </CardFooter>
            </Card>
            <Card className='business-card' variant='primary'>
              <CardImage src='/images/schedule_now.png' alt='Schedule Now' alignment='top-center' />
              <CardBody alignment='top-center'>
                <div className='card-body-title'>Upgrade at your convenience</div>
              </CardBody>
              <CardFooter alignment='top-center'>
                <Button
                  variant='primary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() =>
                    void store.checkAllowStartScheduleUpgrade(
                      index,
                      allowScheduleNow[sap.id],
                      bypassAcknowledgements[sap.id],
                    )
                  }
                  data-testid={`schedule-now-${sap.id}-btn`}
                >
                  <Loader loading={loaders.scheduleNow}>Schedule Now</Loader>
                </Button>
              </CardFooter>
            </Card>
            <Card className='business-card' variant='primary'>
              <CardImage src='/images/trupanion.png' alt='Trupanion' alignment='top-center' />
              <CardBody alignment='top-center'>
                <div className='card-body-title'>Trupanion Integration</div>
              </CardBody>
              <CardFooter alignment='top-center'>
                <Button
                  variant='primary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() => store.redirectToTrupanion(index)}
                  data-testid={`trupanion-${sap.id}-btn`}
                >
                  Configure
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      ))}
    </div>
  );
});

export default Dashboard;
